import {
  DIVISION_PERCENT_NOT_APPLICABLE,
  as400BranchesForDivision,
} from '@erp/rpc';
import type {
  DivisionPriceChange,
  PricelistDetails,
  UpdatePricelistDetailsInput,
} from '@erp/rpc';
import type { PricelistReceivedAs } from '@erp/shared-types';

/** A division the price change can be recorded against (one per DataSource). */
export interface DivisionOption {
  id: string;
  title: string;
}

/**
 * Which divisions carry branches (and their letters) lives with the other
 * AS/400 static mappings: AS400_BRANCHES_BY_DIVISION in @erp/rpc.
 */
function branchesForDivision(division: DivisionOption): readonly string[] {
  return as400BranchesForDivision(division.title);
}

/**
 * Key of one percentage field: the division alone (its DataSource id), or one
 * of its branches (`<dataSourceId>:<branchCode>`).
 */
export function entryKey(dataSourceId: string, branchCode?: string): string {
  return branchCode ? `${dataSourceId}:${branchCode}` : dataSourceId;
}

/** One percentage field a division can show. */
export interface DivisionEntry {
  key: string;
  /** Absent on the division-wide entry. */
  branchCode?: string;
  /** What the field is labelled: "%RD" or "%RD A". */
  label: string;
}

/** Branch codes that appear in the values for this division's keys. */
function branchCodesInValues(
  values: PricelistDetailsFormValues,
  dataSourceId: string,
): string[] {
  const prefix = `${dataSourceId}:`;
  return Object.keys(values.percentByEntryKey)
    .filter((key) => key.startsWith(prefix))
    .map((key) => key.slice(prefix.length));
}

/**
 * The fields a division can carry: the division-wide one, its configured
 * branches, plus any saved branch no longer configured (so old data stays
 * visible and editable rather than silently dropped).
 */
export function divisionEntries(
  division: DivisionOption,
  values: PricelistDetailsFormValues,
): DivisionEntry[] {
  const known = branchesForDivision(division);
  const extras = branchCodesInValues(values, division.id)
    .filter((branchCode) => !known.includes(branchCode))
    .sort();
  return [
    { key: entryKey(division.id), label: division.title.toUpperCase() },
    ...[...known, ...extras].map((branchCode) => ({
      key: entryKey(division.id, branchCode),
      branchCode,
      label: `${division.title.toUpperCase()} ${branchCode}`,
    })),
  ];
}

/** One division's row in the price-change table, plus its branch rows. */
export interface DivisionRow {
  division: DivisionOption;
  /** The division-wide entry — the row's own percentage. */
  entry: DivisionEntry;
  /** The branch entries, shown when the row is expanded. */
  branches: DivisionEntry[];
}

/**
 * Every division as a row, whether or not it has a percentage yet. Rows are
 * always all of them: the table is where a division is given a value, so
 * hiding the ones without one would hide the thing the user came to do.
 */
export function divisionRows(
  divisions: DivisionOption[],
  values: PricelistDetailsFormValues,
): DivisionRow[] {
  return divisions.map((division) => {
    const entries = divisionEntries(division, values);
    return {
      division,
      entry: {
        key: entryKey(division.id),
        label: division.title.toUpperCase(),
      },
      branches: entries.filter((entry) => entry.branchCode !== undefined),
    };
  });
}

/** Whether one entry currently carries a percentage. */
export function hasPercent(
  values: PricelistDetailsFormValues,
  key: string,
): boolean {
  return (values.percentByEntryKey[key]?.trim() ?? '') !== '';
}

/**
 * A percentage as typed: optionally negative, up to 4 decimal places. Mirrors
 * the contract's percent schema so the form rejects what the API would.
 */
export const PERCENT_PATTERN = /^-?\d{1,3}(\.\d{1,4})?$/;

/** "N/A", "NA", "n/a" — however a "does not apply" is typed. */
const NOT_APPLICABLE_PATTERN = /^n\/?a$/i;

/**
 * A division percentage as it is saved: N/A in its one spelling for a division
 * reviewed and marked as not applying (GEN-2576), anything else trimmed. Blank
 * means "not reviewed yet" and is not saved at all.
 */
function normalizeDivisionPercent(typed: string): string {
  const trimmed = typed.trim();
  return NOT_APPLICABLE_PATTERN.test(trimmed)
    ? DIVISION_PERCENT_NOT_APPLICABLE
    : trimmed;
}

/** Whether a division field reads as N/A, however it was typed. */
export function isNotApplicable(typed: string): boolean {
  return normalizeDivisionPercent(typed) === DIVISION_PERCENT_NOT_APPLICABLE;
}

/** Whether a division field holds something the API will accept. */
function isValidDivisionPercent(typed: string): boolean {
  const percent = normalizeDivisionPercent(typed);
  return (
    percent === DIVISION_PERCENT_NOT_APPLICABLE || PERCENT_PATTERN.test(percent)
  );
}

/** The editable details, held as raw input text so partial typing is allowed. */
export interface PricelistDetailsFormValues {
  /** Entry key (division or division-branch) → price change percent, as typed. */
  percentByEntryKey: Record<string, string>;
  /**
   * The vendor-stated anticipated change as FREE TEXT (GEN-2577): a number,
   * a range ("4 to 10%"), or several per-group lines. Not validated as a
   * percentage — the name keeps its old suffix for the field it maps to.
   */
  anticipatedPercent: string;
  fromTariffChange: boolean;
  /** YYYY-MM-DD as a native date input gives it, or '' when unset. */
  effectiveDate: string;
  /** YYYY-MM-DD as a native date input gives it, or '' when unset. */
  erpUploadDate: string;
  /** What the vendor sent (notice / file / both), or '' when unset. */
  receivedAs: PricelistReceivedAs | '';
  /** YYYY-MM-DD as a native date input gives it, or '' when unset. */
  receivedDate: string;
  notes: string;
}

/**
 * The saved domain values, whichever record holds them.
 *
 * `PricelistDetails` minus `updatedAt`, because a pricelist update case has
 * every one of these fields but keeps its own `updatedAt` on the case rather
 * than inside the details object. Everything below works on the VALUES, so
 * the narrower type is what lets one form serve both.
 */
export type PricelistDetailValues = Omit<PricelistDetails, 'updatedAt'>;

/** The saved details as form text. */
export function valuesFromDetails(
  details: PricelistDetailValues,
): PricelistDetailsFormValues {
  const percentByEntryKey: Record<string, string> = {};
  for (const change of details.divisionPriceChanges) {
    percentByEntryKey[entryKey(change.dataSourceId, change.branchCode)] =
      change.percent;
  }
  return {
    percentByEntryKey,
    anticipatedPercent: details.anticipatedVendorChangePercent ?? '',
    fromTariffChange: details.fromTariffChange,
    effectiveDate: details.effectiveDate ?? '',
    erpUploadDate: details.erpUploadDate ?? '',
    receivedAs: details.receivedAs ?? '',
    receivedDate: details.receivedDate ?? '',
    notes: details.notes ?? '',
  };
}

/** Blank-insensitive comparison, for the unsaved-changes state. */
export function valuesEqual(
  a: PricelistDetailsFormValues,
  b: PricelistDetailsFormValues,
): boolean {
  if (a.fromTariffChange !== b.fromTariffChange) return false;
  if (a.anticipatedPercent.trim() !== b.anticipatedPercent.trim()) return false;
  if (a.effectiveDate !== b.effectiveDate) return false;
  if (a.erpUploadDate !== b.erpUploadDate) return false;
  if (a.receivedAs !== b.receivedAs) return false;
  if (a.receivedDate !== b.receivedDate) return false;
  if (a.notes.trim() !== b.notes.trim()) return false;
  const keys = new Set([
    ...Object.keys(a.percentByEntryKey),
    ...Object.keys(b.percentByEntryKey),
  ]);
  for (const key of keys) {
    const left = a.percentByEntryKey[key]?.trim() ?? '';
    const right = b.percentByEntryKey[key]?.trim() ?? '';
    if (left !== right) return false;
  }
  return true;
}

/**
 * Every filled-in DIVISION percentage that isn't a valid percentage. The
 * anticipated change is free text since GEN-2577 and is never invalid.
 */
export function invalidPercentKeys(
  values: PricelistDetailsFormValues,
): string[] {
  const invalid: string[] = [];
  for (const [key, percent] of Object.entries(values.percentByEntryKey)) {
    if (percent.trim() !== '' && !isValidDivisionPercent(percent)) {
      invalid.push(key);
    }
  }
  return invalid;
}

/**
 * The save payload: only fields with a percentage are stored, each stamped
 * with its DataSource's current title, and blank text becomes null.
 */
export function buildDetailsPayload(
  values: PricelistDetailsFormValues,
  divisions: DivisionOption[],
): Omit<UpdatePricelistDetailsInput, 'pdfDocumentId'> {
  const divisionPriceChanges: DivisionPriceChange[] = [];
  for (const division of divisions) {
    for (const entry of divisionEntries(division, values)) {
      const percent = normalizeDivisionPercent(
        values.percentByEntryKey[entry.key] ?? '',
      );
      if (percent === '') continue;
      divisionPriceChanges.push({
        dataSourceId: division.id,
        divisionCode: division.title.toUpperCase(),
        ...(entry.branchCode ? { branchCode: entry.branchCode } : {}),
        percent,
      });
    }
  }
  const anticipated = values.anticipatedPercent.trim();
  const notes = values.notes.trim();
  return {
    divisionPriceChanges,
    notes: notes === '' ? null : notes,
    fromTariffChange: values.fromTariffChange,
    anticipatedVendorChangePercent: anticipated === '' ? null : anticipated,
    effectiveDate: values.effectiveDate === '' ? null : values.effectiveDate,
    erpUploadDate: values.erpUploadDate === '' ? null : values.erpUploadDate,
    receivedAs: values.receivedAs === '' ? null : values.receivedAs,
    receivedDate: values.receivedDate === '' ? null : values.receivedDate,
  };
}
